import React from 'react';
import { NearbyPathogenicVariant } from '../types';

interface NearbyVariantsCardProps {
  variants: NearbyPathogenicVariant[];
  queryPosition: number | null;
  queryGene: string;
}

const NearbyVariantsCard: React.FC<NearbyVariantsCardProps> = ({ variants, queryPosition, queryGene }) => {
  if (!queryPosition) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
        <span>🎯</span> Pathogenic Variants Near This Position
      </h3>

      {variants.length === 0 ? (
        <p className="text-sm text-slate-400 italic">
          No pathogenic/likely pathogenic variants found within ±5 residues of position {queryPosition} in ClinVar for {queryGene}.
        </p>
      ) : (
        <>
          <p className="text-xs text-slate-400 mb-3">
            {variants.length} P/LP variant{variants.length !== 1 ? 's' : ''} within ±5 residues of position {queryPosition}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide pb-2 pr-4">Variant</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide pb-2 pr-4">Classification</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide pb-2 pr-4">Condition</th>
                  <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wide pb-2">Distance</th>
                </tr>
              </thead>
              <tbody>
                {variants.slice(0, 10).map((v, idx) => (
                  <tr key={idx} className={`border-b border-slate-50 ${v.distance === 0 ? 'bg-amber-50/50' : ''}`}>
                    <td className="py-2 pr-4 font-mono text-sm">
                      {v.clinvarUrl ? (
                        <a
                          href={v.clinvarUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-600 hover:text-brand-800 underline decoration-brand-300 hover:decoration-brand-500 transition-colors"
                        >
                          {v.proteinChange}
                        </a>
                      ) : (
                        <span className="text-slate-700">{v.proteinChange}</span>
                      )}
                      {v.distance === 0 && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
                          SAME RESIDUE
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      <span className={`text-xs font-medium ${
                        v.classification.toLowerCase().includes('likely pathogenic')
                          ? 'text-orange-600'
                          : 'text-red-600'
                      }`}>
                        {v.classification}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-slate-500 text-xs max-w-[200px] truncate">
                      {v.condition || '—'}
                    </td>
                    <td className="py-2 text-right text-slate-400 text-xs">
                      {v.distance === 0 ? '0' : `±${v.distance}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {variants.length > 10 && (
            <p className="text-xs text-slate-400 mt-2">
              Showing 10 of {variants.length} nearby variants.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default NearbyVariantsCard;
